const players = new WeakMap();
let library;

function loadPlayer() {
  return library ||= import("hls-player");
}

export function videoLevelCap(video, levels) {
  const bounds = video.getBoundingClientRect();
  if (!bounds.width || !bounds.height) return -1;
  const ratio = video.closest("video-media")?.hasAttribute("autoplay") ? 1 : Math.min(devicePixelRatio || 1, 2);
  // Allow rounding differences (e.g. a 384px rendition in a 390px slot).
  const index = levels.findIndex(level => level.width >= bounds.width * ratio * 0.98 && level.height >= bounds.height * ratio * 0.98);
  return index < 0 ? levels.length - 1 : index;
}

/** Prepare enough video ahead of visibility to keep scrolling and playback responsive. */
export function prepareVideo(video) {
  if (!video?.dataset.hlsSrc) return Promise.resolve(video);
  if (players.has(video)) return players.get(video).ready;

  let finish;
  const state = { ready: new Promise(resolve => { finish = resolve; }), hls: null, disposed: false, fallback: false };
  players.set(video, state);
  const mp4 = video.dataset.mp4Src;
  let timer;

  const ready = () => {
    clearTimeout(timer);
    finish(state.disposed ? null : video);
  };
  const fallback = () => {
    if (state.disposed || state.fallback) return;
    state.fallback = true;
    const resume = !video.paused;
    const time = video.currentTime;
    state.resize?.disconnect();
    state.hls?.destroy();
    state.hls = null;
    video.src = mp4;
    video.preload = "auto";
    video.addEventListener("loadedmetadata", () => {
      if (state.disposed) return;
      if (time > 0 && time < video.duration) video.currentTime = time;
      if (resume) video.play().catch(() => {});
    }, { once: true, signal: state.abort.signal });
    video.load();
    ready();
  };
  state.abort = new AbortController();
  state.cleanup = () => {
    clearTimeout(timer);
    state.disposed = true;
    state.abort.abort();
    state.resize?.disconnect();
    state.hls?.destroy();
    ready();
  };

  const canPlayNative = video.canPlayType("application/vnd.apple.mpegurl");
  const useNative = () => {
    if (state.disposed || state.fallback) return;
    state.resize?.disconnect();
    state.hls?.destroy();
    state.hls = null;
    video.addEventListener("error", fallback, { once: true, signal: state.abort.signal });
    video.src = video.dataset.hlsSrc;
    video.preload = "auto";
    video.load();
    ready();
  };
  if (!globalThis.MediaSource && !globalThis.ManagedMediaSource) {
    canPlayNative ? useNative() : fallback();
  } else {
    timer = setTimeout(fallback, 8000);
    loadPlayer().then(({ default: Hls }) => {
      if (state.disposed || state.fallback) return;
      if (!Hls.isSupported()) return canPlayNative ? useNative() : fallback();
      const hls = state.hls = new Hls({
        startLevel: 0,
        // Decode incoming bytes instead of waiting for a whole segment download.
        progressive: true,
        maxBufferLength: 6,
        maxMaxBufferLength: 10,
        backBufferLength: 30,
        loopBackBufferFlush: false,
      });
      const updateSize = () => {
        if (state.fallback || state.disposed || state.hls !== hls) return;
        const cap = videoLevelCap(video, hls.levels);
        if (cap >= 0) hls.autoLevelCapping = cap;
      };
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        updateSize();
        if (globalThis.ResizeObserver) {
          state.resize = new ResizeObserver(updateSize);
          state.resize.observe(video);
        }
        window.addEventListener("resize", updateSize, { signal: state.abort.signal });
        ready();
      });
      hls.on(Hls.Events.ERROR, (_event, data) => {
        if (data.fatal) fallback();
      });
      hls.loadSource(video.dataset.hlsSrc);
      hls.attachMedia(video);
    }).catch(() => canPlayNative ? useNative() : fallback());
  }
  return state.ready;
}

export function releaseVideo(video) {
  const state = players.get(video);
  if (!state) return;
  state.cleanup();
  players.delete(video);
}

// Start the hero and nearby videos without waiting for the larger theme/app modules.
// The video component handles later scrolling and dynamically inserted sections.
function warmInitialVideos() {
  document.querySelectorAll("video[data-hls-src]").forEach(video => {
    if (players.has(video)) return;
    const slide = video.closest(".slideshow__slide");
    if (slide && !slide.classList.contains("is-selected")) return;
    const bounds = video.getBoundingClientRect();
    if (bounds.width > 0 && bounds.height > 0 && bounds.top < innerHeight + 1200 && bounds.bottom > 0 && bounds.left < innerWidth + 600 && bounds.right > -600) {
      prepareVideo(video).then(target => {
        // Autoplay can begin while the larger theme module is loading.
        // Nearby videos are only buffered; their component owns later playback.
        if (!target?.isConnected || customElements.get("video-media")) return;
        if (!target.closest("video-media")?.hasAttribute("autoplay")) return;
        const currentSlide = target.closest(".slideshow__slide");
        if (currentSlide && !currentSlide.classList.contains("is-selected")) return;
        const current = target.getBoundingClientRect();
        if (current.width > 0 && current.height > 0 && current.top < innerHeight && current.bottom > 0 && current.left < innerWidth && current.right > 0) {
          target.play().catch(() => {});
        }
      });
    }
  });
}

if (document.readyState === "loading") {
  const observer = new MutationObserver(() => {
    if (!document.querySelector("video[data-hls-src]")) return;
    // Stop before app widgets begin mutating the document. Repeated geometry
    // reads during parsing force layout and delay the media we're preparing.
    observer.disconnect();
    requestAnimationFrame(warmInitialVideos);
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
  document.addEventListener("DOMContentLoaded", () => {
    observer.disconnect();
    warmInitialVideos();
  }, { once: true });
}
warmInitialVideos();
